import { environment } from '@env/environment';

export type ListingPhotoVariant = 'thumb' | 'medium' | 'full';

const DEFAULT_LISTING_PHOTO_STORAGE_BASE =
  'https://storage.googleapis.com/gabali-realty.appspot.com/PropertyPictures/';

export function listingPhotoStorageBase(): string {
  const bucket = environment.firebaseConfig?.storageBucket;
  if (!bucket) {
    return DEFAULT_LISTING_PHOTO_STORAGE_BASE;
  }

  return `https://storage.googleapis.com/${bucket}/PropertyPictures/`;
}

function listingPhotoFileName(index: number, variant: ListingPhotoVariant): string {
  if (variant === 'thumb') {
    return `${index}-thumb.webp`;
  }

  if (variant === 'medium') {
    return `${index}-medium.webp`;
  }

  return `${index}.jpg`;
}

export function buildListingPhotoUrl(
  listingId: string,
  index: number,
  variant: ListingPhotoVariant,
): string {
  return `${listingPhotoStorageBase()}${listingId}/${listingPhotoFileName(index, variant)}`;
}

/** Resolve sold-listing card thumbnails from API paths or listing id. */
export function resolveSoldListingThumbnailUrl(
  listingId: string | null | undefined,
  thumbnail?: string | null,
): string | null {
  const id = String(listingId ?? '').trim();
  const thumb = String(thumbnail ?? '').trim();

  if (thumb.startsWith('http://') || thumb.startsWith('https://')) {
    return thumb;
  }

  if (thumb && id) {
    const filename = thumb.includes('/') ? thumb.split('/').pop() ?? thumb : thumb;
    return `${listingPhotoStorageBase()}${id}/${filename}`;
  }

  if (id) {
    return buildListingPhotoUrl(id, 0, 'thumb');
  }

  return null;
}

function readPhotoCount(property: Record<string, unknown>): number | null {
  const count = property['PhotoCount'];
  if (typeof count === 'number' && Number.isFinite(count) && count > 0) {
    return Math.trunc(count);
  }

  return null;
}

function readStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((path): path is string => typeof path === 'string' && path.length > 0);
}

export function listingPhotoUrls(
  property: Record<string, unknown>,
  variant: ListingPhotoVariant,
): string[] {
  const count = readPhotoCount(property);
  const listingId = String(property['ListingID'] ?? '').trim();
  if (count !== null && listingId) {
    return Array.from({ length: count }, (_, index) =>
      buildListingPhotoUrl(listingId, index, variant),
    );
  }

  if (variant === 'thumb') {
    const cardPaths = readStringArray(property['ImagePathsCard']);
    if (cardPaths.length > 0) {
      return cardPaths;
    }

    return readStringArray(property['ImagePaths']);
  }

  if (variant === 'medium') {
    const mediumPaths = readStringArray(property['ImagePathsMedium']);
    if (mediumPaths.length > 0) {
      return mediumPaths;
    }

    return readStringArray(property['ImagePaths']);
  }

  const fullPaths = readStringArray(property['ImagePaths']);
  if (fullPaths.length > 0) {
    return fullPaths;
  }

  return readStringArray(property['ImagePathsCard']);
}
